// Global error handler that can be set by AurigaContext
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { KPI_DIMENSION_CONFIG_MAP } from '@constants/page';
import { forEach, uniq } from 'lodash';

// Key used to store the timestamp of the last credential-related reload in localStorage
const RELOAD_GUARD_KEY = 'lastCredentialReload';
// Minimum time (ms) between reloads to prevent infinite reload loops when auth infrastructure is unavailable
const RELOAD_COOLDOWN_MS = 10_000; // 10 seconds

/**
 * Safely reloads the page for re-authentication, with loop detection.
 * Preserves the current URL for post-auth redirect.
 * @returns true if reload was triggered, false if loop was detected and reload was skipped.
 */
export const safeReloadForReauth = (): boolean => {
  const lastReload = Number(localStorage.getItem(RELOAD_GUARD_KEY) || 0);
  const now = Date.now();

  if (now - lastReload < RELOAD_COOLDOWN_MS) {
    console.error('Reload loop detected. Aborting auto-reload for re-authentication.');
    return false;
  }

  localStorage.setItem(RELOAD_GUARD_KEY, String(now));
  // Preserve current location so handleRedirectAfterAuth can restore it after re-authentication
  localStorage.setItem('redirectAfterAuth', window.location.href);
  window.location.reload();
  return true;
};

let globalErrorHandler: ((message: string) => void) | null = null;

/**
 * Sets the global error handler function
 */
export const setGlobalErrorHandler = (handler: (message: string) => void) => {
  globalErrorHandler = handler;
};

/**
 * Surface error message(s) from any error type
 * @param error - The error object
 * @returns One or more toasts of Error message(s) from any error type
 */
export const surfaceErrorMessages = (error: unknown) => {
  if (error && typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;

    // Expects that API errors will be one or more GraphQL Errors if we have an array
    if (Array.isArray(errorObj.errors) && errorObj.errors.length > 0) {
      const gqlErrorResult: GraphQLResult = error as GraphQLResult;
      forEach(gqlErrorResult?.errors, (gqlError, idx) => {
        if (idx === 0) console.debug('Received error: ', gqlError); // Log at least the first error to console
        // Toast error message
        if (gqlError?.message) globalErrorHandler?.(gqlError.message);
      });

      // Surface output of any partial data received for debugging
      if (gqlErrorResult?.data) {
        console.debug('Received partial data: ', gqlErrorResult.data);
      }
    } else if (typeof errorObj.message === 'string') {
      // Check for direct message property
      globalErrorHandler?.(errorObj.message);
    } else {
      // Output unknown error to console and surface toast
      console.error('Unknown error:', error);
      globalErrorHandler?.('Unknown error');
    }
  }
};

/**
 * Handles errors globally using the registered handler
 * Used at: src/index.tsx
 */
export const handleGlobalError = (error: unknown) => {
  // Detect expired/missing credentials — when session is dead, force a User re-authentication via reload
  if (error instanceof Error && error.name === 'NoCredentials') {
    console.warn('Session credentials expired. Reloading to re-authenticate.');
    safeReloadForReauth();
    return;
  }

  if (globalErrorHandler) {
    // A % denominator rejection is shown, readably, by the save that made it, so only the rest of such
    // an error is surfaced here; the raw denominator text would stack on the readable toast.
    const { readable, unrecognised } = splitDenominatorSaveError(error);
    if (readable.length > 0) {
      if (unrecognised.length > 0) globalErrorHandler(unrecognised.join('; '));
      return;
    }
    surfaceErrorMessages(error);
  } else {
    // No global handler available, so output the error to the console
    console.error('Global Error:', error);
  }
};

/** A dimension's label for a driver field (`revgp` and `revgp_hierarchy` are both "RevGP"). */
const driverFieldLabel = (field: string): string =>
  Object.values(KPI_DIMENSION_CONFIG_MAP).find(
    (config) =>
      config?.decklineDrivers?.valueAccessor === field || config?.decklineDrivers?.hierarchyAccessor === field,
  )?.label ?? field;

/** One backend rejection of a row: `<page_id>/<deckline_id>: <reason>`, or `deckline <deckline_id>: <reason>`. */
const ROW_REJECTION = /^(?:(?:.*?)\/|deckline )([A-Za-z0-9]+): (.*)$/s;

/**
 * Where the backend's "; " join starts a new problem: before a row prefix, or a page's
 * driver-completeness summary (`page <page_id> [<DATASET>]: …`). A "; " inside one problem (the linked
 * denominator rejection has one) is not a boundary.
 */
const PROBLEM_BOUNDARY = /;\s+(?=(?:[^;]*?\/[A-Za-z0-9]+|deckline [A-Za-z0-9]+|page [^;]*?\[[A-Z]+\]): )/;

/**
 * Plain-language text for one row's % denominator rejection, or null when the reason is something else.
 * @param reason - The backend's reason for the row
 * @param rowName - The row's name
 * @param rowScoped - Whether the reason came in the row-prefixed form. Only the denominator check
 *   prefixes `kpi_map incomplete` with a row; bare, the same text is the generic driver-completeness
 *   error, which also covers a row's own KPI and calculated operands.
 */
const describeDenominatorRejection = (reason: string, rowName: string, rowScoped: boolean): string | null => {
  const missing = rowScoped ? reason.match(/kpi_map incomplete — missing fields: \[([^\]]*)\]/) : null;
  if (missing) {
    const dimensions = uniq(
      missing[1]
        .split(',')
        .map((field) => field.trim().replace(/^'|'$/g, ''))
        .filter(Boolean)
        .map(driverFieldLabel),
    );
    return `${rowName}: the denominator can't be resolved; missing ${dimensions.join(', ')}. Set it on the page's % denominator drivers or pick a variant.`;
  }
  const dataset = reason.match(/only GALAXY denominators are supported, not (\S+)/);
  if (dataset) return `${rowName}: only Galaxy KPIs can be used as a denominator (this one is ${dataset[1]}).`;
  if (/its denominator is linked to root/.test(reason)) {
    return `${rowName}: the denominator is linked to a KPI, so its id can't be edited directly. Pick the denominator KPI and variant on the row's Denominator tab.`;
  }
  if (/denominator variant \S+ does not exist under root/.test(reason)) {
    return `${rowName}: the denominator variant no longer exists. Pick another variant.`;
  }
  if (/the page has no denominator_drivers for dataset/.test(reason)) {
    return "Set the page's % denominator drivers in Page Setup before giving rows a base denominator.";
  }
  if (/a calculated denominator needs a formula/.test(reason)) {
    return `${rowName}: a calculated denominator needs a formula.`;
  }
  if (/base denominators on a page whose columns override dimensions are not supported yet/.test(reason)) {
    return `${rowName}: base denominators aren't supported yet on a page whose columns override dimensions.`;
  }
  return null;
};

export interface DenominatorSaveErrorParts {
  /** Plain-language messages for the % denominator rejections, one per row. */
  readable: string[];
  /** The parts of the error that are not denominator rejections, verbatim. */
  unrecognised: string[];
}

/**
 * Splits a SaveDeck error into its % denominator rejections, made readable, and everything else. The
 * backend joins a save's problems with "; " (denominator ones alongside, e.g., driver-completeness
 * failures), so each part is judged on its own. It also repeats a row's rejection for each grain
 * sibling, under the sibling's deckline id, so rows are deduplicated by name: a rejection whose row
 * cannot be named is kept only when no row in the error could be.
 * @param error - The SaveDeck error (a GraphQL result with `errors`, or an Error)
 * @param rowNameFor - The name of a row by deckline id, for the rows the caller has
 */
export const splitDenominatorSaveError = (
  error: unknown,
  rowNameFor: (decklineId: string) => string | null | undefined = () => null,
): DenominatorSaveErrorParts => {
  const errorObj = (error ?? {}) as { errors?: Array<{ message?: string }>; message?: unknown };
  const messages = Array.isArray(errorObj.errors) ? errorObj.errors.map((e) => e?.message ?? '') : [];
  if (typeof errorObj.message === 'string') messages.push(errorObj.message);

  const named: string[] = [];
  const unnamed: string[] = [];
  const unrecognised: string[] = [];
  messages.forEach((message) => {
    message
      .split(PROBLEM_BOUNDARY)
      .filter(Boolean)
      .forEach((part) => {
        const row = part.match(ROW_REJECTION);
        const name = row ? rowNameFor(row[1]) : null;
        const readable = row
          ? describeDenominatorRejection(row[2], name || 'A row', true)
          : describeDenominatorRejection(part, 'A row', false);
        if (!readable) unrecognised.push(part);
        else (name ? named : unnamed).push(readable);
      });
  });
  return { readable: uniq(named.length > 0 ? named : unnamed), unrecognised: uniq(unrecognised) };
};

/**
 * Plain-language messages for a SaveDeck error's % denominator rejections, one per row; empty when the
 * error has none. See {@link splitDenominatorSaveError}.
 * @param error - The SaveDeck error
 * @param rowNameFor - The name of a row by deckline id, for the rows the caller has
 */
export const readableDenominatorSaveErrors = (
  error: unknown,
  rowNameFor?: (decklineId: string) => string | null | undefined,
): string[] => splitDenominatorSaveError(error, rowNameFor).readable;
